import { useCallback, useRef, useState } from "react";
import {
  Check,
  ChevronDown,
  FileCode2,
  Search,
  FileDiff,
  GitBranch,
  GitCommitHorizontal,
  GitMerge,
  GitPullRequestArrow,
  Minus,
  Plus,
  RefreshCw,
  Send,
  Sparkles,
  Trash2,
  Undo2,
  X,
} from "lucide-react";
import { t } from "../../i18n";
import { api } from "../../api";
import type {
  ChangedFile,
  DevelopmentSelection,
  Result,
} from "../chat/ChatDevelopmentPanel.types";
import { parseStatus, selectionBody } from "../chat/ChatDevelopmentPanel.types";

import { GitDiffDialog } from "./GitDiffDialog";
import { GitActionDialog, type GitAction } from "./GitActionDialog";
import { gitChangedEvent, useGitRefresh } from "./useGitRefresh";

export type GitTabProps = {
  conversationId: string;
  selection: DevelopmentSelection;
  onOpenFile: (path: string) => void;
  hasUnsavedChanges?: boolean;
  active?: boolean;
};

export function GitPanel({
  conversationId,
  selection,
  onOpenFile,
  hasUnsavedChanges = false,
  active = true,
}: GitTabProps) {
  const unsavedChanges = useRef(hasUnsavedChanges);
  unsavedChanges.current = hasUnsavedChanges;
  const [files, setFiles] = useState<ChangedFile[]>([]);
  const [branch, setBranch] = useState("");
  const [branches, setBranches] = useState<string[]>([]);
  const [message, setMessage] = useState("");
  const [mergeSource, setMergeSource] = useState("");
  const [mergeOpen, setMergeOpen] = useState(false);
  const [branchManagerOpen, setBranchManagerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [dialog, setDialog] = useState<GitAction | null>(null);
  const [branchName, setBranchName] = useState("");
  const [diffPath, setDiffPath] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [feedback, setFeedback] = useState("");
  const operation = useRef(false);
  const refreshing = useRef<Promise<void> | null>(null);
  const { workspaceId, repositoryId } = selection;
  const request = useCallback(
    (action: string, body: Record<string, unknown> = {}) =>
      api<Result>(
        `/conversations/${conversationId}/development-workspace/git/${action}`,
        {
          method: "POST",
          notifyDataChange: false,
          body: JSON.stringify({
            ...selectionBody({ workspaceId, repositoryId }),
            ...body,
          }),
        },
      ),
    [conversationId, workspaceId, repositoryId],
  );
  const refresh = useCallback(
    async (afterOperation = false) => {
      if (operation.current && !afterOperation) return;
      if (refreshing.current) return refreshing.current;
      setIsRefreshing(true);
      const pending = (async () => {
        try {
          const [status, refs] = await Promise.all([
            request("status"),
            request("branches"),
          ]);
          if (status.code !== 0)
            throw new Error(
              status.stderr.trim() ||
                status.stdout.trim() ||
                "Could not load Git status",
            );
          const parsed = parseStatus(status.stdout);
          setFiles(parsed.files);
          setBranch(parsed.branch);
          if (refs.code === 0)
            setBranches(
              refs.stdout
                .split("\n")
                .filter(Boolean)
                .map((row) => row.slice(1).trim()),
            );
          setLoaded(true);
        } catch (reason) {
          setError(
            reason instanceof Error ? reason.message : "Could not refresh Git",
          );
        } finally {
          setIsRefreshing(false);
        }
      })();
      refreshing.current = pending;
      await pending;
      refreshing.current = null;
    },
    [request],
  );
  const root = useGitRefresh(refresh, active);
  const git = async (action: string, body: Record<string, unknown> = {}) => {
    if (operation.current) return;
    if (
      unsavedChanges.current &&
      [
        "checkout",
        "create-branch",
        "pull",
        "merge",
        "revert",
        "discard",
      ].includes(action)
    ) {
      setError(
        t(
          "Save or discard your unsaved editor changes before changing the working tree.",
        ),
      );
      return;
    }
    operation.current = true;
    setBusy(true);
    setError("");
    setFeedback("");
    let result: Result | undefined;
    try {
      await refreshing.current;
      result = await request(action, body);
      if (result.code !== 0)
        setError(
          result.stderr.trim() ||
            result.stdout.trim() ||
            `Git ${action} failed`,
        );
      else setFeedback(result.stdout.trim() || t("Git operation completed"));
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : `Git ${action} failed`,
      );
    } finally {
      // Failed merges and pulls can still change the index and working tree.
      await refresh(true);
      window.dispatchEvent(new Event(gitChangedEvent));
      operation.current = false;
      setBusy(false);
    }
    return result;
  };
  const generateCommitMessage = async () => {
    if (operation.current) return;
    operation.current = true;
    setBusy(true);
    setError("");
    try {
      const result = await api<{ message: string }>(
        `/conversations/${conversationId}/development-workspace/git/generate-commit-message`,
        {
          method: "POST",
          body: JSON.stringify(selectionBody(selection)),
        },
      );
      setMessage(result.message);
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not generate a commit message",
      );
    } finally {
      operation.current = false;
      setBusy(false);
    }
  };
  const ask = (action: GitAction) => {
    setError("");
    setBranchName("");
    setDialog(action);
  };
  const createBranch = () =>
    ask({
      action: "create-branch",
      title: t("Create branch"),
      description: t(
        "Create and switch to a new branch from the current HEAD.",
      ),
      input: true,
    });
  const changeBranch = (value: string) => {
    if (value === "__new") createBranch();
    else if (value !== branch) void git("checkout", { branch: value });
  };
  const deleteBranch = (name: string) =>
    ask({
      action: "delete-branch",
      title: t("Delete branch"),
      description: t("Delete branch “{{name}}”? {{reason}}", {
        name,
        reason: t("Only fully merged branches can be deleted."),
      }),
      body: { branch: name, confirm: true },
      destructive: true,
    });
  const mergeBranches = branches.filter((name) => name !== branch);
  const hasStaged = files.some((file) => file.staged);
  const hasConflicts = files.some((file) =>
    ["UU", "AA", "DD", "AU", "UD", "UA", "DU"].includes(file.code),
  );
  const startMerge = () => {
    setMergeSource(mergeBranches[0] || "");
    setMergeOpen(true);
    setError("");
  };
  const submitMerge = () =>
    ask({
      action: "merge",
      title: t("Merge branch"),
      description: `Merge ${mergeSource} into ${branch}? Resolve any conflicts in the file list if Git reports them.`,
      body: { branch: mergeSource, confirm: true },
    });
  const group = (title: string, staged: boolean) => {
    const allRows = files.filter((file) =>
      staged ? file.staged : file.unstaged,
    );
    const rows = allRows.filter((file) =>
      file.path.toLowerCase().includes(filter.toLowerCase()),
    );
    return (
      <section className="border-b border-stone-200 dark:border-white/10">
        <div className="flex h-9 items-center px-3 text-[11px] font-bold tracking-wider text-stone-500 uppercase">
          <button
            className="flex min-w-0 items-center rounded py-1 focus-visible:ring-2 focus-visible:ring-lumen-500"
            aria-expanded={!collapsed[title]}
            onClick={() =>
              setCollapsed((value) => ({ ...value, [title]: !value[title] }))
            }
          >
            <ChevronDown
              size={13}
              className={collapsed[title] ? "-rotate-90" : ""}
            />
            <span className="ml-1">{title}</span>
          </button>
          <span className="ml-auto flex items-center gap-1">
            <span className="rounded-full bg-stone-100 px-2 py-0.5 dark:bg-white/10">
              {allRows.length}
            </span>
            {allRows.length > 0 && (
              <button
                className="rounded p-1 text-stone-500 hover:bg-stone-200 hover:text-stone-800 disabled:opacity-40 dark:hover:bg-white/10 dark:hover:text-stone-200"
                title={staged ? t("Unstage all") : t("Stage all")}
                aria-label={staged ? t("Unstage all") : t("Stage all")}
                disabled={busy || !loaded}
                onClick={(event) => {
                  event.stopPropagation();
                  void git(staged ? "unstage-all" : "stage-all");
                }}
              >
                {staged ? <Minus size={14} /> : <Plus size={14} />}
              </button>
            )}
          </span>
        </div>
        {!collapsed[title] &&
          rows.map((file) => (
            <div
              key={`${staged}-${file.path}`}
              className="group flex h-9 min-w-0 cursor-pointer items-center gap-2 px-3 text-xs hover:bg-stone-100 dark:hover:bg-white/5"
            >
              <FileCode2 size={14} className="shrink-0 text-stone-400" />
              <button
                className="min-w-0 flex-1 truncate rounded py-2 text-left focus-visible:ring-2 focus-visible:ring-lumen-500"
                title={file.path}
                onClick={() => onOpenFile(file.path)}
              >
                {file.path.split("/").pop()}{" "}
                <span className="text-stone-500">
                  {file.path.includes("/")
                    ? file.path.slice(0, file.path.lastIndexOf("/"))
                    : ""}
                </span>
              </button>
              <button
                className="rounded p-1.5 text-stone-500 hover:bg-stone-200 dark:hover:bg-white/10"
                title={t("Compare with HEAD")}
                aria-label={t("Compare with HEAD")}
                onClick={() => setDiffPath(file.path)}
              >
                <FileDiff size={14} />
              </button>
              <span
                className={`font-mono font-bold ${["UU", "AA", "DD", "AU", "UD", "UA", "DU"].includes(file.code) ? "text-red-600 dark:text-red-400" : "text-amber-700 dark:text-amber-400"}`}
              >
                {staged ? file.code[0] : file.code.trim() || "M"}
              </span>
              <button
                className="rounded p-1.5 text-stone-500 hover:bg-stone-200 disabled:opacity-40 dark:hover:bg-white/10"
                disabled={busy || !loaded}
                aria-label={staged ? t("Unstage") : t("Stage")}
                title={staged ? t("Unstage") : t("Stage")}
                onClick={(event) => {
                  event.stopPropagation();
                  void git(staged ? "unstage" : "stage", { path: file.path });
                }}
              >
                {staged ? <Minus size={14} /> : <Plus size={14} />}
              </button>
              {!staged && (
                <button
                  className="rounded p-1.5 text-red-600 hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-950/30"
                  disabled={busy || !loaded}
                  aria-label={t("Discard changes")}
                  title={t("Discard changes")}
                  onClick={(event) => {
                    event.stopPropagation();
                    ask({
                      action: "discard",
                      title: t("Discard changes"),
                      description: `Discard changes to ${file.path}? This cannot be undone.`,
                      body: { path: file.path, confirm: true },
                      destructive: true,
                    });
                  }}
                >
                  <Undo2 size={13} />
                </button>
              )}
            </div>
          ))}
      </section>
    );
  };
  return (
    <div
      ref={root}
      className="flex h-full min-h-0 min-w-0 flex-col"
      aria-busy={busy}
    >
      <div className="flex flex-wrap items-center gap-1 border-b border-stone-200 p-2 dark:border-white/10">
        <div className="relative min-w-24 flex-1">
          <GitBranch
            size={14}
            className="pointer-events-none absolute top-2.5 left-2.5 text-stone-400"
          />
          <select
            className="field h-9 truncate py-1 pl-8 text-xs"
            value={branch}
            aria-label={t("Current branch")}
            disabled={busy || !loaded}
            onChange={(event) => changeBranch(event.target.value)}
          >
            {!branches.includes(branch) && (
              <option value={branch}>{branch || t("Loading…")}</option>
            )}
            {branches.map((name) => (
              <option key={name}>{name}</option>
            ))}
            <option value="__new">{t("+ Create new branch…")}</option>
          </select>
        </div>
        <button
          className="button h-9 p-2"
          title={t("Manage branches")}
          aria-label={t("Manage branches")}
          disabled={busy || !loaded}
          onClick={() => setBranchManagerOpen((open) => !open)}
        >
          <GitBranch size={14} />
        </button>
        <button
          className="button h-9 p-2"
          title={t("Refresh")}
          aria-label={t("Refresh")}
          disabled={busy || isRefreshing}
          onClick={() => {
            setError("");
            void refresh();
          }}
        >
          <RefreshCw
            size={14}
            className={busy || isRefreshing ? "motion-safe:animate-spin" : ""}
          />
        </button>
        <button
          className="button h-9 p-2"
          title={t("Pull")}
          aria-label={t("Pull")}
          disabled={busy || !loaded}
          onClick={() => void git("pull")}
        >
          <GitPullRequestArrow size={14} />
        </button>
        <button
          className="button h-9 p-2"
          title={t("Merge branch")}
          disabled={busy || !branch || mergeBranches.length === 0}
          onClick={startMerge}
        >
          <GitMerge size={14} />
        </button>
        <button
          className="button h-9 p-2"
          title={t("Push")}
          aria-label={t("Push")}
          disabled={busy || !loaded}
          onClick={() =>
            ask({
              action: "push",
              title: t("Push"),
              description: `Push ${branch} to origin?`,
              body: { confirm: true },
            })
          }
        >
          <Send size={14} />
        </button>
      </div>
      {branchManagerOpen && (
        <div className="border-b border-stone-200 bg-stone-50 p-3 dark:border-white/10 dark:bg-white/3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold">
            <GitBranch size={14} className="text-lumen-600" />
            <span>{t("Manage branches")}</span>
            <button
              className="button-primary ml-auto h-8 px-2.5 text-xs"
              disabled={busy || !loaded}
              onClick={createBranch}
            >
              <Plus size={13} /> {t("Create branch")}
            </button>
          </div>
          <div className="max-h-40 overflow-y-auto rounded-lg border border-stone-200 bg-white dark:border-white/10 dark:bg-white/5">
            {branches.map((name) => (
              <div
                key={name}
                className="flex h-8 items-center gap-2 border-b border-stone-100 px-2 text-xs last:border-b-0 dark:border-white/5"
              >
                <span className="min-w-0 flex-1 truncate" title={name}>
                  {name}
                </span>
                {name === branch && (
                  <span className="text-[10px] text-lumen-600">
                    {t("Current")}
                  </span>
                )}
                <button
                  className="rounded p-1 text-stone-500 hover:bg-red-50 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-red-950/30"
                  title={t("Delete branch")}
                  aria-label={t("Delete branch {{name}}", { name })}
                  disabled={busy || name === branch}
                  onClick={() => deleteBranch(name)}
                >
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-stone-500">
            {t("Only fully merged branches can be deleted.")}
          </p>
        </div>
      )}
      {mergeOpen && (
        <div className="border-b border-stone-200 bg-stone-50 p-3 dark:border-white/10 dark:bg-white/3">
          <div className="mb-2 flex items-center gap-2 text-xs font-semibold">
            <GitMerge size={14} className="text-lumen-600" />
            <span>{t("Merge branch")}</span>
            <button
              className="ml-auto rounded p-1 text-stone-500 hover:bg-stone-200 dark:hover:bg-white/10"
              title={t("Cancel")}
              aria-label={t("Cancel")}
              onClick={() => setMergeOpen(false)}
            >
              <X size={13} />
            </button>
          </div>
          <select
            className="field h-9 py-1 text-xs"
            value={mergeSource}
            onChange={(event) => setMergeSource(event.target.value)}
            disabled={busy || !loaded}
          >
            {mergeBranches.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          <div className="mt-2 flex items-center justify-between gap-2">
            <span className="text-[11px] text-stone-500">
              {t("Merged into")} <strong>{branch}</strong>
            </span>
            <button
              className="button-primary h-8 px-3 text-xs"
              disabled={busy || !mergeSource}
              onClick={submitMerge}
            >
              <GitMerge size={13} /> {t("Merge")}
            </button>
          </div>
        </div>
      )}
      <label className="mx-3 my-2 flex items-center gap-2 rounded border border-stone-200 px-2 dark:border-white/10">
        <Search size={13} className="shrink-0 text-stone-500" />
        <input
          aria-label={t("Filter changed files")}
          placeholder={t("Filter changed files")}
          className="h-8 min-w-0 flex-1 bg-transparent text-xs outline-none focus-visible:ring-2 focus-visible:ring-lumen-500"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      </label>
      {hasConflicts && (
        <p
          role="status"
          className="px-3 py-2 text-xs text-red-700 dark:text-red-300"
        >
          {t(
            "Resolve conflicts in the affected files, then stage them to continue.",
          )}
        </p>
      )}
      <div className="min-h-0 flex-1 overflow-y-auto">
        {!loaded && (
          <p role="status" className="p-4 text-xs text-stone-500">
            {error ? t("Git status unavailable") : t("Loading Git status…")}
          </p>
        )}
        {group(t("Staged changes"), true)}
        {group(t("Changes"), false)}
        {filter &&
          !files.some((file) =>
            file.path.toLowerCase().includes(filter.toLowerCase()),
          ) && (
            <p role="status" className="p-4 text-center text-xs text-stone-500">
              {t("No matching files")}
            </p>
          )}
        {loaded && files.length === 0 && (
          <div className="grid h-40 place-items-center text-sm text-stone-400">
            <span className="text-center">
              <Check size={22} className="mx-auto mb-2 text-emerald-500" />
              {t("No changes")}
            </span>
          </div>
        )}
      </div>
      <div className="border-t border-stone-200 px-3 py-2 text-[11px] text-stone-500 dark:border-white/10">
        {!loaded
          ? t("Loading Git status…")
          : files.length === 0
            ? t("Working tree clean")
            : `${t("unstaged files", {
                count: files.filter((file) => file.unstaged).length,
              })}, ${t("staged files", {
                count: files.filter((file) => file.staged).length,
              })}`}
      </div>
      <div className="border-t border-stone-200 p-3 dark:border-white/10">
        <div className="flex flex-wrap gap-2">
          <input
            className="field h-9 min-w-0 flex-1 basis-full py-1 text-sm"
            aria-label={t("Commit message")}
            placeholder={t("Commit message")}
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            onKeyDown={(event) => {
              if (
                event.key === "Enter" &&
                !busy &&
                loaded &&
                hasStaged &&
                !hasConflicts &&
                message.trim()
              )
                void git("commit", { message }).then((result) => {
                  if (result?.code === 0) setMessage("");
                });
            }}
          />
          <button
            className="button h-9 shrink-0 p-2"
            title={t("Generate commit message with the current model")}
            aria-label={t("Generate commit message with the current model")}
            disabled={busy || !hasStaged || hasConflicts}
            onClick={() => void generateCommitMessage()}
          >
            <Sparkles
              size={14}
              className={busy ? "motion-safe:animate-pulse" : ""}
            />
          </button>
          <button
            className="button-primary h-9 shrink-0 px-3 text-xs"
            disabled={busy || !message.trim() || !hasStaged || hasConflicts}
            onClick={() =>
              void git("commit", { message }).then((result) => {
                if (result?.code === 0) setMessage("");
              })
            }
          >
            <GitCommitHorizontal size={14} /> {t("Commit")}
          </button>
        </div>
        {error && (
          <div
            role="alert"
            className="mt-2 rounded-lg bg-red-50 p-2 text-xs whitespace-pre-wrap text-red-700 dark:bg-red-950/30 dark:text-red-300"
          >
            {error}
          </div>
        )}
        {feedback && (
          <p
            role="status"
            className="mt-2 max-h-20 overflow-auto text-xs whitespace-pre-wrap text-stone-600 dark:text-stone-300"
          >
            {feedback}
          </p>
        )}
        {busy && (
          <p role="status" className="mt-2 text-xs text-stone-500">
            {t("Running Git operation…")}
          </p>
        )}
      </div>
      {diffPath && (
        <GitDiffDialog
          conversationId={conversationId}
          selection={selection}
          path={diffPath}
          onClose={() => setDiffPath(null)}
        />
      )}
      {dialog && (
        <GitActionDialog
          action={dialog}
          value={branchName}
          onChange={setBranchName}
          busy={busy}
          error={error}
          onClose={() => setDialog(null)}
          onSubmit={() => {
            void git(
              dialog.action,
              dialog.input ? { branch: branchName.trim() } : dialog.body,
            ).then((result) => {
              if (result?.code === 0) {
                setDialog(null);
                setMergeOpen(false);
              }
            });
          }}
        />
      )}
    </div>
  );
}
